/* =============================================
   LIGHTBOX — shared photo viewer behaviour
   Lifted out of sections/main-article-establishment.liquid so the product
   page's "About the place" gallery opens the same viewer. Markup shell is
   snippets/article-lightbox.liquid; styles are component-lightbox.css.
   Any section that renders the shell and marks its triggers with
   [data-lightbox] gets this for free — load it with a deferred <script>.
   ============================================= */
(function () {
  function init() {
    // Lightbox. Any button carrying [data-lightbox] opens it; the arrows cycle
    // within that button's data-lightbox-group, so the photo cluster and the
    // carousel behave as two separate galleries rather than one long one.
    const box = document.getElementById('ArticleLightbox');

    if (box) {
      // Reparent to <body>. A fixed-position element is positioned against the
      // nearest ancestor with a transform/filter/contain rather than the
      // viewport, and Dawn puts transforms on section wrappers for its
      // scroll-reveal animations — which would crop the backdrop.
      if (box.parentElement !== document.body) document.body.appendChild(box);

      // Images typed into the rich text fields have no markup we control, so
      // they get promoted to triggers here. They join the group of whichever
      // copy block they sit in, so arrows never wander between sections.
      document.querySelectorAll('[data-lightbox-scope]').forEach((scope) => {
        const name = scope.dataset.lightboxScope;

        scope.querySelectorAll('img').forEach((img) => {
          if (img.closest('[data-lightbox]')) return;

          const src = img.currentSrc || img.src;
          if (!src) return;

          img.classList.add('article-lightbox-trigger');
          img.setAttribute('role', 'button');
          img.setAttribute('tabindex', '0');
          img.setAttribute('aria-label', 'View photo');
          img.dataset.lightbox = '';
          img.dataset.lightboxGroup = name;
          img.dataset.full = src.includes('?') ? `${src}&width=2400` : `${src}?width=2400`;
          img.dataset.alt = img.alt || '';

          img.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              img.click();
            }
          });
        });
      });

      const image = box.querySelector('[data-lightbox-image]');
      const counter = box.querySelector('[data-lightbox-count]');
      const prevBtn = box.querySelector('[data-lightbox-prev]');
      const nextBtn = box.querySelector('[data-lightbox-next]');

      let group = [];
      let index = 0;
      let opener = null;
      let savedScroll = 0;

      // How many photos either side of the current one to fetch ahead. Two is
      // enough to cover a quick double-press without pulling half a 35-photo
      // gallery down for someone who opened one picture and closed it again.
      const PRELOAD_RADIUS = 2;

      const preload = (i) => {
        const el = group[(i + group.length) % group.length];
        if (!el || el.dataset.preloaded) return;
        el.dataset.preloaded = '1';
        const warm = new Image();
        // Low priority so a prefetch never competes with the photo the
        // viewer is actually waiting on.
        warm.fetchPriority = 'low';
        warm.src = el.dataset.full;
      };

      // Nearest first: at radius 2 the order is +1, -1, +2, -2, so a forward
      // press is covered before anything two steps away is even requested.
      const preloadAround = (i) => {
        for (let step = 1; step <= PRELOAD_RADIUS; step += 1) {
          preload(i + step);
          preload(i - step);
        }
      };

      const show = (i) => {
        index = (i + group.length) % group.length;
        const el = group[index];
        const full = el.dataset.full;

        // Paint the thumbnail the page already downloaded, then swap in the
        // full-size file once it lands. Setting src to the full file directly
        // leaves the viewer on an empty frame for the length of the fetch,
        // which is what made opening a photo feel slow.
        const thumb = el.querySelector ? el.querySelector('img') : null;
        const placeholder = (thumb && (thumb.currentSrc || thumb.src)) || el.currentSrc || el.src || '';

        image.alt = el.dataset.alt || '';

        if (placeholder && placeholder !== full) {
          image.src = placeholder;
          image.classList.add('is-loading');
          const loader = new Image();
          loader.onload = () => {
            // A fast second arrow press may have moved on already.
            if (group[index] !== el) return;
            image.src = full;
            image.classList.remove('is-loading');
          };
          loader.src = full;
        } else {
          image.src = full;
          image.classList.remove('is-loading');
        }

        counter.textContent = group.length > 1 ? `${index + 1} / ${group.length}` : '';
        prevBtn.hidden = group.length < 2;
        nextBtn.hidden = group.length < 2;

        if (group.length > 1) preloadAround(index);
      };

      const open = (el) => {
        const name = el.dataset.lightboxGroup || 'default';
        group = Array.from(document.querySelectorAll(`[data-lightbox][data-lightbox-group="${name}"]`));
        opener = el;
        box.hidden = false;
        // Pinning the body is what actually holds the page still. Setting
        // `body { overflow: hidden }` alone did not: html is the scrolling
        // element here, so the page kept moving behind the lightbox.
        // Offsetting by the saved scroll position keeps the page visually
        // where it was while it is pinned.
        savedScroll = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScroll}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        // Marks the whole document, not just the lightbox. The header sits at
        // z-index 500 and the chat widget sets its own from inside a shadow
        // root we cannot reach — so both are suppressed by class instead of
        // out-stacked.
        document.documentElement.classList.add('lightbox-open');
        show(group.indexOf(el));
        requestAnimationFrame(() => box.classList.add('is-open'));
        (group.length > 1 ? nextBtn : box.querySelector('[data-lightbox-close]')).focus();
      };

      const close = () => {
        box.classList.remove('is-open');
        // Unpinned and restored immediately, not after the fade — the page
        // is still covered by the lightbox, so the jump back is not visible.
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, savedScroll);
        window.setTimeout(() => {
          box.hidden = true;
          image.src = '';
          // Released with the fade, not before it, so the header does not
          // reappear while the lightbox is still on screen.
          document.documentElement.classList.remove('lightbox-open');
        }, 200);
        if (opener) opener.focus({ preventScroll: true });
      };

      document.querySelectorAll('[data-lightbox]').forEach((el) => {
        el.addEventListener('click', () => open(el));
      });

      prevBtn.addEventListener('click', () => show(index - 1));
      nextBtn.addEventListener('click', () => show(index + 1));
      box.querySelector('[data-lightbox-close]').addEventListener('click', close);

      // Clicking the backdrop closes; clicking the photo or a control doesn't.
      box.addEventListener('click', (event) => {
        if (event.target === box || event.target.classList.contains('article-lightbox__figure')) close();
      });

      // Swipe, matching the arrows. Threshold is 40px so a tap or a small
      // drag while pinch-zooming does not count, and the horizontal distance
      // must beat the vertical one so a scroll gesture is never read as a
      // swipe. Passive listeners: this never calls preventDefault, so the
      // browser keeps its own pinch-zoom and scroll behaviour.
      let touchX = 0;
      let touchY = 0;
      let swiping = false;

      box.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) { swiping = false; return; }
        touchX = event.touches[0].clientX;
        touchY = event.touches[0].clientY;
        swiping = true;
      }, { passive: true });

      box.addEventListener('touchend', (event) => {
        if (!swiping || group.length < 2) return;
        swiping = false;
        const touch = event.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - touchX;
        const dy = touch.clientY - touchY;
        if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
        show(dx < 0 ? index + 1 : index - 1);
      }, { passive: true });

      document.addEventListener('keydown', (event) => {
        if (box.hidden) return;
        if (event.key === 'Escape') close();
        if (event.key === 'ArrowLeft' && group.length > 1) show(index - 1);
        if (event.key === 'ArrowRight' && group.length > 1) show(index + 1);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
